import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, SlidersHorizontal, X, ChevronDown, ChevronRight, LayoutGrid, List, TrendingUp, Home, Store, Wrench, ShieldCheck, MessageCircle } from 'lucide-react';
import seo from '../seo/seoConfig.json';
import bikeCollections from '../content/bikeCollections.json';
import { loadCatalog, itemPrice, KIND_LABELS } from '../search/catalog';
import { search, POPULAR_SEARCHES } from '../search/engine';
import { slug } from '../search/text';
import { CURRENCIES, priceIn, approxPrice, formatMoney, priceBounds, parseRange, toCurrency } from '../search/money';
import NotFound from './NotFound';
import ChristmasPillars from '../components/home/ChristmasPillars';
import { NAVIDAD, ageShort } from '../seo/navidadText';
import TitleText from '../components/common/TitleText';

// The shop: every product of the store (e-bikes, analog Raymon bikes, Christmas bikes and
// accessories) with search, filters, sorting and "load more". /tienda shows everything; /ebikes,
// /bicicletas-analogas, /accesorios, /catalogo, /category/<slug> and /bicicletas/<collection> are
// the same page with filters preset by the route. Removing or changing a preset filter moves to
// /tienda, so from any section you can jump to another without the menu. All state lives in the URL.
const PAGE_SIZE = 24;
const KIND_ORDER = { ebike: 0, bike: 1, navidad: 2, accessory: 3 };
const KIND_PLURAL = { ebike: 'E-Bikes', bike: 'Bicicletas análogas', navidad: 'Bicis de Navidad', accessory: 'Accesorios' };

const CATEGORY_LABELS = { 'e-mtb': 'E-MTB', 'e-gravel': 'E-Gravel', 'e-trekking': 'E-Trekking', 'e-city': 'E-City', road: 'de ruta', gravel: 'Gravel', mtb: 'MTB', trekking: 'Trekking', kids: 'para niños' };

const PRESETS = {
    '/tienda': { fixed: {}, h1: 'Todos los productos', intro: 'Bicicletas Raymon, e-bikes, bicis de Navidad para niños y accesorios de ciclismo. Busca por nombre, marca o categoría, o usa los filtros.' },
    '/ebikes': { fixed: { tipo: ['ebike'] }, h1: 'E-Bikes Raymon', intro: 'Bicicletas eléctricas Raymon: E-MTB, e-gravel, e-trekking y e-city con motores Bosch, Yamaha y DJI. Quita el filtro para ver toda la tienda.' },
    '/bicicletas-analogas': { fixed: { tipo: ['bike'] }, h1: 'Bicicletas análogas', intro: 'Bicicletas Raymon sin asistencia eléctrica: montaña, ruta, gravel, trekking y niños. Quita el filtro para ver toda la tienda.' },
    '/catalogo': { fixed: { tipo: ['ebike', 'bike'] }, h1: 'Bicicletas Raymon', intro: 'Todas las bicicletas Raymon, eléctricas y análogas, con precio en dólares y tabla de tallas en cada ficha.' },
    '/accesorios': { fixed: { tipo: ['accessory'] }, h1: 'Accesorios', intro: 'Cascos, luces, guantes, puños, herramientas, ropa y más para tu bicicleta, con precio en pesos.' },
    // The Christmas campaign page: same shop, with its own copy, quick filters and the layaway cards below
    '/navidad': { fixed: { tipo: ['navidad'] }, h1: 'Bicicletas de Navidad para niños', intro: 'Raleigh, Julen BMX y Carolina Princess en aros 12, 16 y 20, armadas y calibradas en nuestro taller, con plan de apartado para Nochebuena y Reyes. Retíralas en Los Prados, Santo Domingo.', title: NAVIDAD.title, pillars: true, quick: ['aro', 'genero'] }
};
PRESETS['/bicicletas-navidad'] = PRESETS['/navidad'];

// A collection (src/content/bikeCollections.json) fixes filters written for the old bike catalog;
// here they are the same filters under the shop's names and values
const COLLECTION_FACETS = { rueda: 'aro', categoria: 'cat', suspension: 'suspension', cuadro: 'cuadro', motor: 'motor' };
function collectionFilters(filters) {
    const fixed = {};
    Object.entries(filters || {}).forEach(([key, values]) => {
        if (key === 'precio') {
            // 'hasta-1000' was a dollar range of the old catalog
            const top = /^hasta-(\d+)$/.exec(values[0] || '');
            if (top) {
                fixed.precio = [`0-${top[1]}`];
                fixed.moneda = ['usd'];
            }
            return;
        }
        const facet = COLLECTION_FACETS[key];
        if (!facet) return;
        if (key === 'categoria') fixed[facet] = values.map((id) => (seo.categories[id] || {}).slug).filter(Boolean);
        else fixed[facet] = values.slice();
    });
    return fixed;
}

// What the route fixes and says; null when the category or collection does not exist
function presetFor(path, categorySlug, coleccionSlug) {
    if (coleccionSlug) {
        const collection = bikeCollections.find((c) => c.slug === coleccionSlug);
        if (!collection) return null;
        // Collections are bike pages: accessories stay out even when the filter is only a price
        return { fixed: { tipo: ['ebike', 'bike', 'navidad'], ...collectionFilters(collection.filters) }, quiet: ['tipo'], h1: collection.h1, intro: collection.intro, title: collection.title };
    }
    if (categorySlug) {
        const category = Object.values(seo.categories).find((c) => c.slug === categorySlug);
        if (!category) return null;
        const page = seo.pages[`/category/${category.slug}`] || {};
        return { fixed: { cat: [category.slug] }, h1: `Bicicletas ${CATEGORY_LABELS[category.slug] || category.name}`, intro: page.description || '', title: page.title };
    }
    const preset = PRESETS[path] || PRESETS['/tienda'];
    return { ...preset, title: (seo.pages[path] || {}).title || preset.title || seo.pages['/tienda'].title };
}

// Each facet is one URL parameter; an item passes when nothing is picked or its value is picked.
// Bike-only facets (aro, suspensión, motor, cuadro) disappear by themselves when no bike is listed.
const FACETS = [
    { id: 'tipo', label: 'Tipo de producto', value: (i) => i.kind, options: Object.keys(KIND_ORDER).map((k) => ({ value: k, label: KIND_PLURAL[k] })) },
    // Categories listed by product type (e-bikes, analog bikes, Christmas, accessories), then by how many they hold
    { id: 'cat', label: 'Categoría', value: (i) => i.categoryKey, labelOf: (items, v) => (items.find((i) => i.categoryKey === v) || {}).category || v, group: (i) => KIND_ORDER[i.kind] },
    { id: 'serie', label: 'Serie Raymon', value: (i) => i.serieKey || null, labelOf: (items, v) => (items.find((i) => i.serieKey === v) || {}).serie || v, sort: (a, b) => a.label.localeCompare(b.label) },
    { id: 'marca', label: 'Marca', value: (i) => (i.brand ? slug(i.brand) : null), labelOf: (items, v) => (items.find((i) => i.brand && slug(i.brand) === v) || {}).brand || v },
    {
        id: 'aro',
        label: 'Aro / rueda',
        value: (i) => i.wheel || null,
        // Kids' sizes carry the suggested age (the owner's table): 'Aro 12 · niños de 2 a 4 años'
        labelOf: (items, v) => {
            if (items.some((i) => i.kind === 'navidad' && i.wheel === v)) return `Aro ${v} · niños de ${ageShort({ categoria: v })}`;
            return v.includes('/') ? `Mixta ${v.replace('/', '" / ')}"` : `Aro ${v}"`;
        },
        sort: (a, b) => parseFloat(a.value) - parseFloat(b.value) || a.value.length - b.value.length
    },
    { id: 'genero', label: 'Para', value: (i) => i.gender || null, options: [{ value: 'nina', label: 'Niñas' }, { value: 'nino', label: 'Niños' }, { value: 'unisex', label: 'Unisex' }] },
    {
        id: 'suspension',
        label: 'Suspensión',
        value: (i) => i.suspension || null,
        options: [{ value: 'doble', label: 'Doble suspensión' }, { value: 'delantera', label: 'Solo delantera' }, { value: 'rigida', label: 'Rígida' }]
    },
    { id: 'motor', label: 'Motor', value: (i) => i.motor || null, labelOf: (items, v) => v },
    { id: 'cuadro', label: 'Material del cuadro', value: (i) => i.frame || null, options: [{ value: 'aluminio', label: 'Aluminio' }, { value: 'carbono', label: 'Carbono' }] }
];

const SORTS = [
    { id: 'relevancia', label: 'Relevancia' },
    { id: 'destacados', label: 'Destacados' },
    { id: 'precio-asc', label: 'Precio: menor a mayor' },
    { id: 'precio-desc', label: 'Precio: mayor a menor' },
    { id: 'nombre', label: 'Nombre (A-Z)' },
    { id: 'novedades', label: 'Novedades' }
];

// The sections a visitor can jump to without the menu
const TABS = [
    { label: 'Todo', to: '/tienda', tipo: [] },
    { label: 'E-Bikes', to: '/ebikes', tipo: ['ebike'] },
    { label: 'Bicicletas análogas', to: '/bicicletas-analogas', tipo: ['bike'] },
    { label: 'Bicis de Navidad', to: '/navidad', tipo: ['navidad'] },
    { label: 'Accesorios', to: '/accesorios', tipo: ['accessory'] }
];

// Facts already published elsewhere on the site (footer, taller, Christmas pillars)
const TRUST = [
    { icon: Store, title: 'Distribuidor oficial Raymon', text: 'Tienda física en C/ Eliseo Grullón #26, Los Prados, Santo Domingo.' },
    { icon: Wrench, title: 'Taller propio', text: 'Mantenimiento, ajustes y repuestos en el mismo local.' },
    { icon: ShieldCheck, title: 'Garantía y soporte en tienda', text: 'Cualquier gestión posterior sobre tu bicicleta se atiende aquí.' },
    { icon: MessageCircle, title: 'Asesoría por WhatsApp', text: 'Lunes a viernes de 9 a. m. a 6 p. m. y sábados de 9 a. m. a 4 p. m.' }
];

const sameSet = (a, b) => a.length === b.length && a.every((v) => b.includes(v));
const byName = (a, b) => a.name.localeCompare(b.name);

// Sorting by price compares every product on the chosen scale; products without a price go last
function sortItems(items, sortId, currency) {
    const list = items.slice();
    if (sortId === 'relevancia') return list;
    if (sortId === 'destacados') {
        return list.sort((a, b) => (b.popular - a.popular) || (KIND_ORDER[a.kind] - KIND_ORDER[b.kind]) || ((b.price ? 1 : 0) - (a.price ? 1 : 0)) || byName(a, b));
    }
    if (sortId === 'nombre') return list.sort(byName);
    if (sortId === 'novedades') return list.sort((a, b) => String(b.created || '').localeCompare(String(a.created || '')) || byName(a, b));
    const desc = sortId === 'precio-desc';
    return list.sort((a, b) => {
        const pa = priceIn(a, currency);
        const pb = priceIn(b, currency);
        if (!pa || !pb) return (!pa) - (!pb);
        return (desc ? pb - pa : pa - pb) || byName(a, b);
    });
}

// The options a facet offers for a list: its fixed ones, or the distinct values found
function facetOptions(facet, items) {
    const present = new Map();
    const groups = new Map();
    items.forEach((item) => {
        const value = facet.value(item);
        if (!value) return;
        present.set(value, (present.get(value) || 0) + 1);
        if (facet.group && !groups.has(value)) groups.set(value, facet.group(item));
    });
    if (facet.options) return facet.options.filter((o) => present.has(o.value));
    const options = [...present.keys()].map((value) => ({ value, label: facet.labelOf(items, value) }));
    return options.sort(facet.sort || ((a, b) => (groups.get(a.value) || 0) - (groups.get(b.value) || 0) || present.get(b.value) - present.get(a.value) || a.label.localeCompare(b.label)));
}

const Chips = ({ facet, options, picked, onToggle }) => (
    <div className="flex flex-wrap gap-2">
        {options.map((option) => {
            const active = picked.includes(option.value);
            return (
                <button
                    key={option.value}
                    type="button"
                    aria-pressed={active}
                    disabled={!active && option.count === 0}
                    onClick={() => onToggle(facet.id, option.value)}
                    className={`px-3 py-2 rounded-full border text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${active ? 'bg-black border-black text-white' : 'border-gray-200 text-gray-700 hover:border-black'}`}
                >
                    {option.label}
                    <span className={`ml-1.5 text-xs ${active ? 'text-gray-300' : 'text-gray-400'}`}>{option.count}</span>
                </button>
            );
        })}
    </div>
);

// Two thumbs on one track plus the exact numbers; the URL only changes when a thumb is released
const PriceRange = ({ bounds, value, currency, onChange, onCurrency }) => {
    const [draft, setDraftState] = useState(null);
    // The latest draft, readable from a blur handler created by an earlier render
    const draftRef = useRef(null);
    const setDraft = (next) => { draftRef.current = next; setDraftState(next); };
    const current = draft || {
        min: Math.max(bounds.min, Math.min(value ? value.min : bounds.min, bounds.max)),
        max: Math.min(bounds.max, Math.max(value ? value.max : bounds.max, bounds.min))
    };
    const pct = (v) => ((v - bounds.min) / (bounds.max - bounds.min)) * 100;
    const commit = (next) => {
        const range = next || draftRef.current || current;
        setDraft(null);
        const min = Math.max(bounds.min, Math.min(range.min, range.max - bounds.step));
        const max = Math.min(bounds.max, Math.max(range.max, min + bounds.step));
        onChange(min <= bounds.min && max >= bounds.max ? null : { min, max });
    };
    const box = 'flex items-center rounded-xl bg-gray-50 border border-gray-200 focus-within:border-black';
    const field = 'w-full min-w-0 bg-transparent py-2 pr-2 text-sm text-black focus:outline-none';
    const label = CURRENCIES[currency].label;

    return (
        <div className="space-y-4">
            <div className="inline-flex rounded-full bg-gray-100 p-1" role="group" aria-label="Moneda del filtro de precio">
                {Object.entries(CURRENCIES).map(([key, c]) => (
                    <button key={key} type="button" aria-pressed={currency === key} onClick={() => onCurrency(key)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${currency === key ? 'bg-black text-white' : 'text-gray-600 hover:text-black'}`}>
                        {c.label}
                    </button>
                ))}
            </div>
            <div className="relative h-8 mx-3">
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 rounded bg-gray-200" />
                <div className="absolute top-1/2 -translate-y-1/2 h-1 rounded bg-black" style={{ left: `${pct(current.min)}%`, right: `${100 - pct(current.max)}%` }} />
                <input
                    type="range"
                    className="range-thumb"
                    min={bounds.min}
                    max={bounds.max}
                    step={bounds.step}
                    value={current.min}
                    aria-label={`Precio mínimo en ${label}`}
                    style={{ zIndex: current.min >= bounds.max - bounds.step ? 5 : 3 }}
                    onInput={(e) => setDraft({ ...current, min: Math.min(Number(e.target.value), current.max - bounds.step) })}
                    onChange={(e) => commit({ ...current, min: Math.min(Number(e.target.value), current.max - bounds.step) })}
                />
                <input
                    type="range"
                    className="range-thumb"
                    min={bounds.min}
                    max={bounds.max}
                    step={bounds.step}
                    value={current.max}
                    aria-label={`Precio máximo en ${label}`}
                    style={{ zIndex: 4 }}
                    onInput={(e) => setDraft({ ...current, max: Math.max(Number(e.target.value), current.min + bounds.step) })}
                    onChange={(e) => commit({ ...current, max: Math.max(Number(e.target.value), current.min + bounds.step) })}
                />
            </div>
            <div className="flex items-center gap-2">
                <label className="flex-1 min-w-0">
                    <span className="block text-[10px] uppercase tracking-widest text-gray-400 mb-1">Mínimo</span>
                    <div className={box}>
                        <span className="pl-3 pr-1.5 text-xs text-gray-400 select-none">{label}</span>
                        <input type="number" inputMode="numeric" min={bounds.min} max={bounds.max} step={bounds.step} value={Math.round(current.min)} onChange={(e) => setDraft({ ...current, min: Number(e.target.value) || bounds.min })} onBlur={() => commit(null)} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} className={field} />
                    </div>
                </label>
                <span className="text-gray-300 mt-4">–</span>
                <label className="flex-1 min-w-0">
                    <span className="block text-[10px] uppercase tracking-widest text-gray-400 mb-1">Máximo</span>
                    <div className={box}>
                        <span className="pl-3 pr-1.5 text-xs text-gray-400 select-none">{label}</span>
                        <input type="number" inputMode="numeric" min={bounds.min} max={bounds.max} step={bounds.step} value={Math.round(current.max)} onChange={(e) => setDraft({ ...current, max: Number(e.target.value) || bounds.max })} onBlur={() => commit(null)} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} className={field} />
                    </div>
                </label>
            </div>
            <p className="text-xs text-gray-400">
                {currency === 'usd' ? 'Los precios en pesos se muestran convertidos a 62 pesos por dólar.' : 'Los precios en dólares se muestran convertidos a 62 pesos por dólar.'}
            </p>
        </div>
    );
};

// Filters as an accordion: closed by default, each header shows how many values are picked
const FilterAccordion = ({ sections, open, onOpen }) => (
    <div>
        {sections.map((section) => {
            const isOpen = open.includes(section.id);
            return (
                <div key={section.id} className="border-b border-gray-100">
                    <button type="button" aria-expanded={isOpen} aria-controls={`filter-${section.id}`} onClick={() => onOpen(section.id)} className="w-full flex items-center justify-between gap-3 py-4 text-left group">
                        <span className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-black">
                            {section.label}
                            {section.count > 0 && <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-black text-white text-[10px] font-bold">{section.count}</span>}
                        </span>
                        <ChevronDown size={16} className={`text-gray-400 group-hover:text-black transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                        <div id={`filter-${section.id}`} className="pb-5">
                            {section.content}
                            {section.count > 0 && section.onClear && (
                                <button type="button" onClick={section.onClear} className="mt-3 text-xs font-bold text-gray-400 hover:text-black">Quitar {section.count === 1 ? 'este filtro' : 'estos filtros'}</button>
                            )}
                        </div>
                    )}
                </div>
            );
        })}
    </div>
);

const Price = ({ item, currency, className = 'text-sm md:text-base' }) => {
    const approx = approxPrice(item, currency);
    return (
        <div>
            <p className={`${className} font-bold text-black`}>{itemPrice(item)}</p>
            {approx && <p className="text-[11px] text-gray-400 leading-tight">{approx}</p>}
        </div>
    );
};

const ProductCard = ({ item, currency }) => (
    <Link to={item.url} className="group block" onClick={() => window.scrollTo(0, 0)}>
        <div className="relative bg-white border border-gray-100 rounded-[2rem] p-5 sm:p-7 mb-4 aspect-square sm:aspect-[4/3] flex items-center justify-center transition-all duration-500 shadow-sm group-hover:shadow-xl group-hover:-translate-y-1">
            <span className="absolute top-4 left-4 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{KIND_LABELS[item.kind]}</span>
            {item.image ? (
                <img src={item.image} alt={item.name} className="max-w-full max-h-full object-contain mix-blend-multiply transition-transform duration-500 group-hover:scale-[1.04]" loading="lazy" decoding="async" referrerPolicy="no-referrer" />
            ) : (
                <div className="text-gray-300 text-xs uppercase font-bold tracking-widest">Sin foto</div>
            )}
        </div>
        <div className="px-2 space-y-1">
            <div className="text-[11px] font-black uppercase tracking-wider text-red-600">{item.brand || item.category}</div>
            <h3 className="text-base md:text-lg font-black italic uppercase tracking-tight text-black leading-tight line-clamp-2 pr-1" title={item.name}>{item.name}</h3>
            {item.subtitle && <p className="text-xs text-gray-500 truncate">{item.subtitle}</p>}
            <Price item={item} currency={currency} />
        </div>
    </Link>
);

const ProductRow = ({ item, currency }) => (
    <Link to={item.url} className="group flex items-center gap-5 bg-white border border-gray-100 rounded-3xl p-4 sm:p-5 transition-all duration-500 shadow-sm hover:shadow-lg" onClick={() => window.scrollTo(0, 0)}>
        <div className="w-24 h-24 sm:w-32 sm:h-28 shrink-0 flex items-center justify-center">
            {item.image ? (
                <img src={item.image} alt={item.name} className="max-w-full max-h-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" referrerPolicy="no-referrer" />
            ) : (
                <div className="text-gray-300 text-[10px] uppercase font-bold tracking-widest">Sin foto</div>
            )}
        </div>
        <div className="flex-1 min-w-0">
            <div className="text-[11px] font-black uppercase tracking-wider text-red-600">{item.brand || item.category} <span className="text-gray-400 font-bold">· {KIND_LABELS[item.kind]}</span></div>
            <h3 className="text-lg sm:text-xl font-black italic uppercase tracking-tight text-black leading-tight">{item.name}</h3>
            {item.subtitle && <p className="text-xs text-gray-500 truncate">{item.subtitle}</p>}
        </div>
        <div className="text-right">
            <Price item={item} currency={currency} className="text-base sm:text-xl" />
        </div>
    </Link>
);

// Placeholder cards while the catalog loads, so the page keeps its shape
const SkeletonCards = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-x-5 sm:gap-x-8 gap-y-10" aria-hidden="true">
        {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="animate-pulse">
                <div className="bg-gray-100 rounded-[2rem] aspect-square sm:aspect-[4/3] mb-4" />
                <div className="px-2 space-y-2">
                    <div className="h-2.5 w-16 bg-gray-100 rounded" />
                    <div className="h-4 w-3/4 bg-gray-100 rounded" />
                    <div className="h-3 w-1/2 bg-gray-100 rounded" />
                    <div className="h-4 w-20 bg-gray-100 rounded" />
                </div>
            </div>
        ))}
    </div>
);

const Shop = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { categorySlug, coleccionSlug } = useParams();
    const [params, setParams] = useSearchParams();
    const paramsRef = useRef(params);
    paramsRef.current = params;

    const path = location.pathname.replace(/\/+$/, '') || '/';
    const preset = useMemo(() => presetFor(path, categorySlug, coleccionSlug), [path, categorySlug, coleccionSlug]);
    const fixed = useMemo(() => (preset ? preset.fixed : {}), [preset]);

    const q = (params.get('q') || '').trim();
    const sortParam = params.get('orden');
    const visibleParam = parseInt(params.get('ver'), 10) || PAGE_SIZE;
    // A facet comes from the URL when it is there, otherwise from what the route presets
    const selected = useMemo(() => Object.fromEntries(FACETS.map((f) => [f.id, params.has(f.id) ? params.get(f.id).split(',').filter(Boolean) : (fixed[f.id] || [])])), [params, fixed]);
    const priceParam = params.has('precio') ? params.get('precio') : (fixed.precio ? fixed.precio[0] : null);
    const priceRange = parseRange(priceParam);
    const hasParams = [...params.keys()].length > 0;

    const [items, setItems] = useState(null);
    const [failed, setFailed] = useState(false);
    const [text, setText] = useState(q);
    const [viewMode, setViewMode] = useState('grid');
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    // Filter groups start closed, except the ones the URL already uses
    const [openGroups, setOpenGroups] = useState(() => [...FACETS.filter((f) => (params.get(f.id) || '').length).map((f) => f.id), ...(params.has('precio') ? ['precio'] : []), ...((preset && preset.quick) || [])]);
    const sortRef = useRef(null);

    useEffect(() => {
        loadCatalog().then(setItems).catch((error) => {
            console.error('Error loading the store:', error);
            setFailed(true);
        });
    }, []);

    // Changes one or more URL parameters; any change shows the first page again. Touching a filter
    // the route presets leaves the preset page for /tienda, with every filter written out.
    const update = (changes, replace = false) => {
        const next = new URLSearchParams(paramsRef.current);
        const leaves = Object.keys(changes).some((key) => fixed[key]);
        if (leaves) Object.entries(fixed).forEach(([key, values]) => { if (!next.has(key)) next.set(key, values.join(',')); });
        Object.entries(changes).forEach(([key, value]) => {
            const text = Array.isArray(value) ? value.join(',') : value;
            if (text) next.set(key, text);
            else next.delete(key);
        });
        if (!('ver' in changes)) next.delete('ver');
        if (leaves) navigate({ pathname: '/tienda', search: next.toString() ? `?${next}` : '' }, { replace });
        else setParams(next, { replace });
    };

    // The box on the page follows the URL (header searches land here) and writes back while typing
    useEffect(() => {
        setText((current) => (current.trim() === q ? current : q));
    }, [q]);
    useEffect(() => {
        if (text.trim() === q) return undefined;
        const t = setTimeout(() => update({ q: text.trim() }, true), 150);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text]);

    useEffect(() => {
        if (q && typeof window.gtag === 'function') window.gtag('event', 'view_search_results', { search_term: q });
    }, [q]);

    useEffect(() => { setIsSortOpen(false); }, [location.pathname]);

    // The sort menu closes with a click elsewhere or Escape
    useEffect(() => {
        if (!isSortOpen) return undefined;
        const onClick = (e) => { if (sortRef.current && !sortRef.current.contains(e.target)) setIsSortOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setIsSortOpen(false); };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [isSortOpen]);

    // The page behind the mobile filter sheet stays put
    useEffect(() => {
        if (!filtersOpen) return undefined;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = overflow; };
    }, [filtersOpen]);

    const searched = useMemo(() => (items ? search(items, q) : null), [items, q]);

    if (!preset) return <NotFound />;

    const base = searched ? searched.items : [];
    // Every listing opens cheapest first (owner's rule); a typed search opens by relevance
    const sortId = sortParam && SORTS.some((s) => s.id === sortParam) ? sortParam : (q ? 'relevancia' : 'precio-asc');

    const passesFacets = (item, skip) => FACETS.every((facet) => {
        const picked = selected[facet.id];
        return facet.id === skip || !picked.length || picked.includes(facet.value(item));
    });
    // Products the current facets leave: the price scale and the slider bounds come from these
    const scoped = base.filter((item) => passesFacets(item));

    // Price scale: dollars when everything listed is priced in dollars (Raymon pages), pesos otherwise
    const currencyParam = params.get('moneda') || (fixed.moneda ? fixed.moneda[0] : null);
    const defaultCurrency = scoped.length && scoped.every((item) => !item.price || item.currency === 'USD') ? 'usd' : 'rd';
    const currency = CURRENCIES[currencyParam] ? currencyParam : defaultCurrency;

    const inPrice = (item) => {
        if (!priceRange) return true;
        const value = priceIn(item, currency);
        return value !== null && value >= priceRange.min && value <= priceRange.max;
    };
    const passes = (item, skip) => passesFacets(item, skip) && (skip === 'precio' || inPrice(item));

    const filtered = base.filter((item) => passes(item));
    const sorted = sortItems(filtered, sortId, currency);

    // Each facet offers the values of the products that pass the other filters (on /accesorios the
    // categories are accessory categories), with how many products each one would leave
    const panels = FACETS
        .map((facet) => {
            const others = base.filter((item) => passes(item, facet.id));
            const options = facetOptions(facet, others).map((option) => ({
                ...option,
                count: others.filter((item) => facet.value(item) === option.value).length
            }));
            // A picked value stays visible even when nothing else matches it, so it can be removed
            selected[facet.id].forEach((value) => {
                if (options.some((option) => option.value === value)) return;
                const fixedOption = facet.options && facet.options.find((option) => option.value === value);
                options.push({ value, label: fixedOption ? fixedOption.label : facet.labelOf(base, value), count: 0 });
            });
            return { facet, options };
        })
        .filter(({ facet, options }) => options.length > 1 || selected[facet.id].length);

    const bounds = priceBounds(scoped, currency);

    // A collection fixes "bikes only" silently: that scope is not shown as removable chips
    const quiet = preset.quiet || [];
    const activeFilters = panels.flatMap(({ facet, options }) =>
        options.filter((option) => selected[facet.id].includes(option.value)).map((option) => ({ id: facet.id, value: option.value, label: option.label, fixed: (fixed[facet.id] || []).includes(option.value) }))
    ).filter((filter) => !(filter.fixed && quiet.includes(filter.id)));
    if (priceRange) activeFilters.push({ id: 'precio', value: priceParam, label: `${formatMoney(priceRange.min, currency)} – ${formatMoney(priceRange.max, currency)}`, fixed: Boolean(fixed.precio) });
    // Filters added on top of what the route presets: "Limpiar" removes these and stays on the page
    const extraFilters = activeFilters.filter((f) => !f.fixed);

    const toggle = (facetId, value) => {
        const picked = selected[facetId];
        update({ [facetId]: picked.includes(value) ? picked.filter((v) => v !== value) : [...picked, value] });
    };
    const removeFilter = (filter) => (filter.id === 'precio' ? update({ precio: null }) : toggle(filter.id, filter.value));
    const clearFilters = () => update(Object.fromEntries([...FACETS.map((f) => f.id), 'precio'].filter((id) => !fixed[id]).map((id) => [id, null])));
    const clearAll = () => {
        setText('');
        update({ q: null, orden: null, precio: null, ...Object.fromEntries(FACETS.map((f) => [f.id, null])) });
    };
    const setPrice = (range) => update({ precio: range ? `${Math.round(range.min)}-${Math.round(range.max)}` : null }, true);
    const setCurrency = (key) => {
        if (key === currency) return;
        // The chosen range follows the new scale, rounded to its steps
        const converted = priceRange ? {
            min: Math.floor(toCurrency(priceRange.min, CURRENCIES[currency].code, CURRENCIES[key].code) / CURRENCIES[key].step) * CURRENCIES[key].step,
            max: Math.ceil(toCurrency(priceRange.max, CURRENCIES[currency].code, CURRENCIES[key].code) / CURRENCIES[key].step) * CURRENCIES[key].step
        } : null;
        update({ moneda: key, precio: converted ? `${converted.min}-${converted.max}` : null }, true);
    };
    const toggleGroup = (id) => setOpenGroups((open) => (open.includes(id) ? open.filter((g) => g !== id) : [...open, id]));

    const visibleCount = Math.min(sorted.length, Math.max(PAGE_SIZE, visibleParam));
    const pageItems = sorted.slice(0, visibleCount);
    const loadMore = () => update({ ver: String(visibleCount + PAGE_SIZE) }, true);

    const kindCounts = base.reduce((acc, item) => { acc[item.kind] = (acc[item.kind] || 0) + 1; return acc; }, {});
    const tabCount = (tab) => (tab.tipo.length ? tab.tipo.reduce((n, kind) => n + (kindCounts[kind] || 0), 0) : base.length);
    const activeTab = TABS.find((tab) => sameSet(tab.tipo, selected.tipo) && (tab.tipo.length > 0 || !selected.cat.length));
    const tabUrl = (tab) => (q ? `${tab.to}${tab.to.includes('?') ? '&' : '?'}q=${encodeURIComponent(q)}` : tab.to);
    const title = q ? `${q}: resultados de búsqueda | PROOMTB & ROAD` : preset.title;
    const countLabel = `${sorted.length} ${sorted.length === 1 ? 'producto' : 'productos'}`;

    const sections = [
        ...(bounds ? [{
            id: 'precio',
            label: 'Precio',
            count: priceRange ? 1 : 0,
            onClear: fixed.precio ? null : () => update({ precio: null }),
            content: <PriceRange bounds={bounds} value={priceRange} currency={currency} onChange={setPrice} onCurrency={setCurrency} />
        }] : []),
        ...panels.map(({ facet, options }) => ({
            id: facet.id,
            label: facet.label,
            count: selected[facet.id].length,
            onClear: fixed[facet.id] ? null : () => update({ [facet.id]: null }),
            content: <Chips facet={facet} options={options} picked={selected[facet.id]} onToggle={toggle} />
        }))
    ];

    return (
        <div className="bg-white min-h-screen pt-14 lg:pt-24 pb-20">
            <Helmet>
                <title>{title}</title>
                {hasParams && <meta name="robots" content="noindex, follow" />}
            </Helmet>

            <div className="max-w-[1600px] mx-auto px-6 sm:px-12 lg:px-16">
                <nav className="flex flex-wrap items-center gap-2 text-xs text-gray-400 font-medium uppercase tracking-wider mb-6" aria-label="Ruta">
                    <Link to="/" className="hover:text-black transition-colors flex items-center gap-1"><Home size={13} /> Inicio</Link>
                    <ChevronRight size={13} />
                    {path === '/tienda' && !q ? (
                        <span className="text-black font-bold">Tienda</span>
                    ) : (
                        <>
                            <Link to="/tienda" className="hover:text-black transition-colors">Tienda</Link>
                            <ChevronRight size={13} />
                            <span className="text-black font-bold">{q ? 'Búsqueda' : preset.h1}</span>
                        </>
                    )}
                </nav>

                <div className="mb-8">
                    {q ? (
                        <>
                            <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-2">Resultados de búsqueda{activeTab && activeTab.tipo.length ? ` en ${activeTab.label}` : ''}</p>
                            <h1 className="text-3xl md:text-5xl text-black font-black italic tracking-tight">“{q}”</h1>
                            {searched && searched.partial && (
                                <p className="text-gray-600 mt-4 max-w-3xl">No hay productos con todas las palabras de tu búsqueda. Te mostramos los más parecidos.</p>
                            )}
                        </>
                    ) : (
                        <>
                            <h1 className="text-3xl md:text-5xl text-black font-black italic uppercase tracking-tight"><TitleText text={preset.h1} dark /></h1>
                            {preset.intro && <p className="text-gray-600 mt-4 max-w-3xl text-base md:text-lg">{preset.intro}</p>}
                        </>
                    )}
                </div>

                {/* Sections: jump between e-bikes, analog bikes, Christmas bikes and accessories */}
                <nav className="flex flex-wrap gap-2 mb-8" aria-label="Secciones de la tienda">
                    {TABS.map((tab) => (
                        <Link
                            key={tab.to}
                            to={tabUrl(tab)}
                            aria-current={activeTab === tab ? 'page' : undefined}
                            className={`whitespace-nowrap px-4 sm:px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${activeTab === tab ? 'bg-black text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-black'}`}
                        >
                            {tab.label}
                            {items && <span className={`ml-1.5 text-xs font-medium ${activeTab === tab ? 'text-gray-300' : 'text-gray-400'}`}>{tabCount(tab)}</span>}
                        </Link>
                    ))}
                </nav>

                {/* Filters the campaign page keeps in sight: suggested age by wheel size, and girls / boys */}
                {preset.quick && items && (
                    <div className="mb-8 space-y-3">
                        {preset.quick.map((id) => {
                            const panel = panels.find((p) => p.facet.id === id);
                            if (!panel) return null;
                            return (
                                <div key={id} className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-black uppercase tracking-widest text-gray-400 mr-1">{id === 'aro' ? 'Edad sugerida' : panel.facet.label}</span>
                                    <Chips facet={panel.facet} options={panel.options} picked={selected[id]} onToggle={toggle} />
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="flex gap-12">
                    {/* Filters: sidebar on desktop */}
                    <aside className="hidden lg:block w-72 shrink-0">
                        <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-3 pb-8">
                            <div className="flex items-center justify-between mb-2">
                                <h2 className="text-lg font-black italic uppercase tracking-tight">Filtros</h2>
                                {extraFilters.length > 0 && (
                                    <button onClick={clearFilters} className="text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-black">Limpiar</button>
                                )}
                            </div>
                            {items && <FilterAccordion sections={sections} open={openGroups} onOpen={toggleGroup} />}
                        </div>
                    </aside>

                    <div className="flex-1 min-w-0">
                        {/* Toolbar: stays at the top of the screen on phones while the list scrolls */}
                        <div className="sticky top-20 lg:static z-30 bg-white/95 backdrop-blur-sm lg:bg-white -mx-6 px-6 sm:-mx-12 sm:px-12 lg:mx-0 lg:px-0 pt-2 lg:pt-0 flex flex-wrap items-center gap-3 sm:gap-4 mb-6 text-sm text-gray-500 border-b border-gray-100 pb-4 lg:pb-6">
                            <label className="relative flex-1 min-w-[180px] max-w-md">
                                <span className="sr-only">Buscar productos</span>
                                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="search"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    placeholder={activeTab && activeTab.tipo.length ? `Buscar en ${activeTab.label.toLowerCase()}` : 'Buscar en toda la tienda'}
                                    enterKeyHint="search"
                                    className="w-full pl-11 pr-10 py-3 bg-gray-50 border border-gray-100 rounded-full text-black placeholder:text-gray-400 focus:outline-none focus:border-black [&::-webkit-search-cancel-button]:hidden"
                                />
                                {text && (
                                    <button type="button" onClick={() => { setText(''); update({ q: null }); }} aria-label="Borrar búsqueda" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-black">
                                        <X size={16} />
                                    </button>
                                )}
                            </label>

                            <button
                                onClick={() => setFiltersOpen(true)}
                                className="lg:hidden flex items-center gap-2 px-4 py-3 border border-gray-200 rounded-full font-bold text-black"
                            >
                                <SlidersHorizontal size={16} /> Filtros{extraFilters.length > 0 && ` (${extraFilters.length})`}
                            </button>

                            <div className="flex items-center gap-4 sm:gap-6 ml-auto">
                                {items && <span className="font-medium">{countLabel}</span>}

                                <div className="hidden sm:flex items-center space-x-4">
                                    <button onClick={() => setViewMode('grid')} aria-label="Ver en cuadrícula" aria-pressed={viewMode === 'grid'} className={`p-1 transition-colors ${viewMode === 'grid' ? 'text-black' : 'text-gray-300 hover:text-gray-400'}`}>
                                        <LayoutGrid size={20} />
                                    </button>
                                    <button onClick={() => setViewMode('list')} aria-label="Ver en lista" aria-pressed={viewMode === 'list'} className={`p-1 transition-colors ${viewMode === 'list' ? 'text-black' : 'text-gray-300 hover:text-gray-400'}`}>
                                        <List size={20} />
                                    </button>
                                </div>

                                <div className="relative" ref={sortRef}>
                                    <button onClick={() => setIsSortOpen(!isSortOpen)} aria-haspopup="listbox" aria-expanded={isSortOpen} className="flex items-center cursor-pointer hover:text-black transition-colors font-medium">
                                        <span className="mr-2">{SORTS.find((s) => s.id === sortId).label}</span>
                                        <ChevronDown size={16} className={`transition-transform duration-300 ${isSortOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {isSortOpen && (
                                        <div role="listbox" className="absolute right-0 mt-4 w-56 bg-white border border-gray-100 rounded-2xl shadow-2xl py-4 z-50">
                                            {SORTS.filter((s) => s.id !== 'relevancia' || q).map((option) => (
                                                <button
                                                    key={option.id}
                                                    role="option"
                                                    aria-selected={sortId === option.id}
                                                    onClick={() => { update({ orden: option.id }); setIsSortOpen(false); }}
                                                    className={`w-full text-left px-6 py-3 text-sm transition-colors hover:bg-gray-50 ${sortId === option.id ? 'text-black font-bold' : 'text-gray-500'}`}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Active filters */}
                        {activeFilters.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2 mb-8">
                                {activeFilters.map((filter) => (
                                    <button key={`${filter.id}-${filter.value}`} onClick={() => removeFilter(filter)} className="flex items-center gap-1.5 pl-3 pr-2 py-1.5 bg-gray-100 rounded-full text-sm text-black hover:bg-gray-200" title="Quitar filtro">
                                        {filter.label} <X size={14} />
                                    </button>
                                ))}
                                {extraFilters.length > 0 && <button onClick={clearFilters} className="text-sm font-bold text-gray-400 hover:text-black px-2">Limpiar todo</button>}
                            </div>
                        )}

                        {/* Products */}
                        {failed ? (
                            <div className="text-center py-20">
                                <p className="text-xl text-gray-500 mb-6">No pudimos cargar los productos. Revisa tu conexión e inténtalo de nuevo.</p>
                                <button onClick={() => window.location.reload()} className="bg-black text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-gray-800">Reintentar</button>
                            </div>
                        ) : !items ? (
                            <SkeletonCards />
                        ) : sorted.length === 0 ? (
                            <div className="text-center py-20">
                                <p className="text-xl font-bold text-black">No encontramos productos{q ? ` para “${q}”` : ' con esos filtros'}.</p>
                                <p className="text-gray-500 mt-2">{activeFilters.length ? 'Prueba quitando algún filtro o con otra búsqueda:' : 'Revisa la ortografía o prueba con una de estas búsquedas:'}</p>
                                <div className="flex flex-wrap justify-center gap-2 mt-6">
                                    {POPULAR_SEARCHES.map((p) => (
                                        <Link key={p} to={`/tienda?q=${encodeURIComponent(p)}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-gray-200 text-sm text-gray-700 hover:border-black hover:text-black">
                                            <TrendingUp size={14} className="text-gray-400" /> {p}
                                        </Link>
                                    ))}
                                </div>
                                <button onClick={clearAll} className="mt-8 bg-black text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-gray-800">Ver todos los productos</button>
                            </div>
                        ) : viewMode === 'grid' ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-x-5 sm:gap-x-8 gap-y-10">
                                {pageItems.map((item) => <ProductCard key={item.key} item={item} currency={currency} />)}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {pageItems.map((item) => <ProductRow key={item.key} item={item} currency={currency} />)}
                            </div>
                        )}

                        {/* Load more */}
                        {items && sorted.length > 0 && (
                            <div className="mt-14 flex flex-col items-center gap-4">
                                <p className="text-sm text-gray-500">Has visto {visibleCount} de {sorted.length} {sorted.length === 1 ? 'producto' : 'productos'}</p>
                                <div className="w-48 h-1 rounded-full bg-gray-100 overflow-hidden" aria-hidden="true">
                                    <div className="h-full bg-black rounded-full transition-all duration-500" style={{ width: `${(visibleCount / sorted.length) * 100}%` }} />
                                </div>
                                {visibleCount < sorted.length && (
                                    <button onClick={loadMore} className="w-full sm:w-auto px-10 py-4 bg-black text-white rounded-full text-sm font-black uppercase tracking-widest hover:bg-gray-800 transition-colors">
                                        Cargar {Math.min(PAGE_SIZE, sorted.length - visibleCount)} más
                                    </button>
                                )}
                            </div>
                        )}

                        {preset.pillars && <ChristmasPillars className="mt-16" />}

                        {/* Why buy here: facts the site already states elsewhere */}
                        <section className="mt-20 border-t border-gray-100 pt-12">
                            <h2 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-6">Comprar en PROOMTB & ROAD</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                                {TRUST.map(({ icon: Icon, title: t, text: body }) => (
                                    <div key={t} className="flex items-start gap-4 p-5 bg-zinc-50 rounded-2xl border border-zinc-200/80">
                                        <div className="w-10 h-10 rounded-xl bg-black text-white flex items-center justify-center shrink-0"><Icon size={18} /></div>
                                        <div>
                                            <h3 className="text-sm font-black italic uppercase tracking-tight text-black mb-1">{t}</h3>
                                            <p className="text-xs text-gray-500 leading-relaxed">{body}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <a href="https://wa.me/18297163555" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-6 text-sm font-bold text-black underline underline-offset-4 hover:text-gray-600">
                                <MessageCircle size={16} /> Escríbenos por WhatsApp al (829) 716-3555
                            </a>
                        </section>

                        <section className="mt-16 border-t border-gray-100 pt-12 space-y-8">
                            <div>
                                <h2 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4">Explora por categoría</h2>
                                <div className="flex flex-wrap gap-2">
                                    {Object.values(seo.categories).map((c) => (
                                        <Link key={c.slug} to={`/category/${c.slug}`} className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700 hover:border-black transition-colors">{c.name}</Link>
                                    ))}
                                    <Link to="/navidad" className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700 hover:border-black transition-colors">Bicis de Navidad</Link>
                                    <Link to="/bicicletas-para-ninos-santo-domingo" className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700 hover:border-black transition-colors">Cómo elegir la talla</Link>
                                </div>
                            </div>
                            <div>
                                <h2 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4">Bicicletas por tipo</h2>
                                <div className="flex flex-wrap gap-2">
                                    {bikeCollections.filter((c) => c.slug !== coleccionSlug).map((c) => (
                                        <Link key={c.slug} to={`/bicicletas/${c.slug}`} className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700 hover:border-black transition-colors">{c.short}</Link>
                                    ))}
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {/* Filters: full-screen panel on mobile */}
            {filtersOpen && (
                <div className="lg:hidden fixed inset-0 z-[100] bg-white flex flex-col" role="dialog" aria-modal="true" aria-label="Filtros">
                    <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                        <h2 className="text-lg font-black italic uppercase tracking-tight">Filtros</h2>
                        <button onClick={() => setFiltersOpen(false)} aria-label="Cerrar filtros" className="p-2 -mr-2">
                            <X size={24} />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-6 py-2">
                        <FilterAccordion sections={sections} open={openGroups} onOpen={toggleGroup} />
                    </div>
                    <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
                        <button onClick={clearFilters} disabled={!extraFilters.length} className="px-6 py-4 border border-gray-200 rounded-full text-sm font-bold uppercase tracking-widest disabled:opacity-40">Limpiar</button>
                        <button onClick={() => setFiltersOpen(false)} className="flex-1 py-4 bg-black text-white rounded-full text-sm font-bold uppercase tracking-widest">
                            Ver {countLabel}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// /category/bikes was the old URL of the analog bikes
export const CategoryShop = () => {
    const { categorySlug } = useParams();
    const { search } = useLocation();
    if (categorySlug === 'bikes') return <Navigate to={`/bicicletas-analogas${search}`} replace />;
    return <Shop />;
};

export default Shop;
